import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Upload } from "lucide-react";
import { ModalShell } from "../_dialog";

export function CsvImportDialog({ onClose, onImported }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!file) {
      toast.error("Pick a CSV file first.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/opportunity/prospects/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      toast.success(`Imported ${r.data.created} prospects.`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      title="CSV import"
      eyebrow="Opportunity Engine"
      onClose={onClose}
      testId="opp-csv-dialog"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-semibold rounded-full px-3 py-1.5 text-slate-600 hover:bg-slate-100"
            data-testid="opp-csv-cancel"
          >
            {result ? "Close" : "Cancel"}
          </button>
          {result ? (
            <button
              type="button"
              onClick={onImported}
              data-testid="opp-csv-done"
              className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-800"
            >
              Open prospect list
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={busy || !file}
              data-testid="opp-csv-submit"
              className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-800 disabled:opacity-50"
            >
              <Upload className="w-3 h-3" /> {busy ? "Importing…" : "Import"}
            </button>
          )}
        </>
      }
    >
      <div className="text-xs text-slate-600">
        Supported columns:{" "}
        <code className="text-[11px] bg-slate-100 px-1.5 py-0.5 rounded">
          clinic_name, website, city, state, clinic_type, contact_name, contact_role, contact_email, phone, linkedin_url, source, notes
        </code>
        . Rows missing a <code>clinic_name</code> are skipped. Max 500 per import.
      </div>
      <input
        ref={fileRef}
        type="file"
        accept=".csv,text/csv"
        onChange={(e) => setFile(e.target.files?.[0] || null)}
        data-testid="opp-csv-input"
        className="block w-full text-xs"
      />
      {result && (
        <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs space-y-1" data-testid="opp-csv-result">
          <div><strong className="text-emerald-700">Created:</strong> {result.created}</div>
          <div><strong className="text-slate-600">Skipped (no clinic_name):</strong> {result.skipped}</div>
          {result.errors?.length > 0 && (
            <div className="text-rose-600">{result.errors.join(" · ")}</div>
          )}
        </div>
      )}
    </ModalShell>
  );
}

// ===========================================================================
// Prospect detail dialog (signals + research checklist + AI + tasks + convert)
